import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { toast } from "sonner";
import { Shield, Users, User, Settings } from "lucide-react";

export type RoleKey = 'admin' | 'manager' | 'employee' | 'workspace';
export type NormalizedRole = 'ic' | 'manager' | 'executive' | 'admin';

interface AvailableRole {
  key: RoleKey;
  label: string;
  description: string;
  icon: typeof Shield;
  path: string;
}

interface UseMultiRoleAccessReturn {
  // Normalized role list (used by route guards + nav shaping)
  roles: NormalizedRole[];

  // Raw role flags
  isSuperAdmin: boolean;
  isWorkspaceAdmin: boolean;
  isExecutive: boolean;
  isTeamManager: boolean;
  isIC: boolean;
  
  // Derived permissions
  canAccessAdmin: boolean;
  canAccessSetup: boolean;
  canAccessWorkspaceManagement: boolean;
  canAccessManagerDashboard: boolean;
  canAccessEmployeeDashboard: boolean;
  canAccessExecutiveDashboard: boolean;
  
  // Available views for role selector
  availableRoles: AvailableRole[];
  
  isLoading: boolean;
}

export const useMultiRoleAccess = (): UseMultiRoleAccessReturn => {
  const { currentWorkspace } = useWorkspace();
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isWorkspaceAdmin, setIsWorkspaceAdmin] = useState(false);
  const [isExecutive, setIsExecutive] = useState(false);
  const [isTeamManager, setIsTeamManager] = useState(false);
  const [isIC, setIsIC] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkAllRoles();
  }, [currentWorkspace]);

  const checkAllRoles = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setIsSuperAdmin(false);
        setIsWorkspaceAdmin(false);
        setIsExecutive(false);
        setIsTeamManager(false);
        setIsIC(false);
        setIsLoading(false);
        return;
      }

      // Check 1: Super Admin via user_roles (platform-level, doesn't require workspace)
      const { data: userRoles } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id);

      const superAdmin = userRoles?.some(r => r.role === 'admin') || false;

      // If no workspace selected, only check super admin status
      if (!currentWorkspace) {
        setIsSuperAdmin(superAdmin);
        setIsWorkspaceAdmin(false);
        setIsExecutive(false);
        setIsTeamManager(false);
        setIsIC(false);
        setIsLoading(false);
        return;
      }

      // Check 2: Workspace Admin via workspace_members
      const { data: workspaceMember } = await supabase
        .from('workspace_members')
        .select('workspace_role')
        .eq('workspace_id', currentWorkspace.id)
        .eq('user_id', user.id)
        .eq('is_active', true)
        .maybeSingle();

      const workspaceAdmin = workspaceMember?.workspace_role === 'admin';
      const workspaceExecutive = workspaceMember?.workspace_role === 'executive';

      // Check 3: Team Manager and IC via team_roles
      const { data: teamRoles } = await supabase
        .from('team_roles')
        .select('role, org_level, is_primary_team')
        .eq('user_id', user.id)
        .eq('workspace_id', currentWorkspace.id)
        .eq('is_active', true);

      const teamManager = teamRoles?.some(tr => tr.role === 'manager') || false;
      const ic = teamRoles?.some(tr => tr.org_level === 'ic') || false;

      setIsSuperAdmin(superAdmin);
      setIsWorkspaceAdmin(workspaceAdmin);
      setIsExecutive(workspaceExecutive || workspaceAdmin || superAdmin);
      setIsTeamManager(teamManager);
      setIsIC(ic || (!teamManager && !workspaceExecutive && !workspaceAdmin));
    } catch (error) {
      console.error("Multi-role access check failed:", error);
      toast.error("Failed to verify permissions");
      setIsSuperAdmin(false);
      setIsWorkspaceAdmin(false);
      setIsExecutive(false);
      setIsTeamManager(false);
      setIsIC(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Derived permissions
  const canAccessAdmin = isSuperAdmin;
  const canAccessSetup = isWorkspaceAdmin || isSuperAdmin;
  const canAccessWorkspaceManagement = isWorkspaceAdmin || isSuperAdmin;
  const canAccessManagerDashboard = isTeamManager || isWorkspaceAdmin || isSuperAdmin;
  const canAccessEmployeeDashboard = true; // Everyone can access their own employee view
  const canAccessExecutiveDashboard = isExecutive || isWorkspaceAdmin || isSuperAdmin;

  // Normalized roles list used across UI + guards
  // NOTE: We keep this typed and deduped without widening to `string[]`.
  const roleSet = new Set<NormalizedRole>();
  roleSet.add('ic'); // safe default baseline
  if (isTeamManager) roleSet.add('manager');
  if (canAccessExecutiveDashboard) roleSet.add('executive');
  if (isWorkspaceAdmin || isSuperAdmin) roleSet.add('admin');
  const roles: NormalizedRole[] = Array.from(roleSet);

  // Build available roles array
  const availableRoles: AvailableRole[] = [];

  if (canAccessAdmin) {
    availableRoles.push({
      key: 'admin',
      label: 'Super Admin',
      description: 'Platform-wide administration',
      icon: Shield,
      path: '/admin'
    });
  }

  if (canAccessWorkspaceManagement) {
    availableRoles.push({
      key: 'workspace',
      label: 'Workspace',
      description: 'Settings, integrations, and team management',
      icon: Settings,
      path: '/workspace'
    });
  }

  if (canAccessManagerDashboard) {
    availableRoles.push({
      key: 'manager',
      label: 'Manager',
      description: 'Team analytics and performance reviews',
      icon: Users,
      path: '/manager-dashboard'
    });
  }

  if (canAccessEmployeeDashboard) {
    availableRoles.push({
      key: 'employee',
      label: 'Employee',
      description: 'My tasks, goals, and analytics',
      icon: User,
      path: '/employee-dashboard'
    });
  }

  return {
    roles,
    isSuperAdmin,
    isWorkspaceAdmin,
    isExecutive,
    isTeamManager,
    isIC,
    canAccessAdmin,
    canAccessSetup,
    canAccessWorkspaceManagement,
    canAccessManagerDashboard,
    canAccessEmployeeDashboard,
    canAccessExecutiveDashboard,
    availableRoles,
    isLoading,
  };
};
